import { useEffect, useState, useCallback } from "react";
import {
  getAdminPayouts,
  approvePayout,
} from "../../services/ingroove/adminService";
import PageHeader from "../../components/common/PageHeader";
import PayoutsTable from "../../components/ingroove/payouts/PayoutsTable";
import PayoutApproveModal from "../../components/ingroove/payouts/PayoutApproveModal";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Payouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");

  const hasActiveFilters = fromDate || toDate;

  const fetchPayouts = useCallback(
    async (searchVal, pageVal, status, from, to) => {
      setLoading(true);
      try {
        const res = await getAdminPayouts({
          search: searchVal,
          pageNumber: pageVal,
          pageSize: 20,
          status: status || undefined,
          fromDate: from || undefined,
          toDate: to || undefined,
        });
        setPayouts(res.data);
        setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchPayouts(search, 1, statusFilter, fromDate, toDate);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchPayouts(search, page, statusFilter, fromDate, toDate);
  }, [page]);

  const handleApprove = async () => {
    if (!selectedPayout) return;
    setApproving(true);
    setApproveError("");
    try {
      const res = await approvePayout(selectedPayout.id);
      if (res.isSuccess) {
        setPayouts((prev) =>
          prev.map((p) =>
            p.id === selectedPayout.id ? { ...p, status: "Completed" } : p,
          ),
        );
        setSelectedPayout(null);
      } else {
        setApproveError(res.message ?? "Failed to approve payout.");
      }
    } catch {
      setApproveError("Something went wrong. Please try again.");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payouts"
        subtitle={`${meta.totalRecords.toLocaleString()} payout requests`}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search by hub, event or reference..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
          />
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 bg-bg-surface border border-border rounded-lg p-1">
          {["Pending", "Completed", "Failed", ""].map((s) => (
            <button
              key={s}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                statusFilter === s
                  ? "bg-primary text-white"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {s === "" ? "All" : s}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowFilters((p) => !p)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition ${
            showFilters || hasActiveFilters
              ? "border-primary text-primary bg-primary/10"
              : "border-border text-text-muted hover:border-border-hover"
          }`}
        >
          <SlidersHorizontal size={13} />
          Date
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-red-400 transition"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Date filter panel */}
      {showFilters && (
        <div className="bg-bg-surface border border-border rounded-xl p-4 flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>
        </div>
      )}

      <PayoutsTable
        payouts={payouts}
        loading={loading}
        onApprove={(payout) => {
          setSelectedPayout(payout);
          setApproveError("");
        }}
      />

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} payouts
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      <PayoutApproveModal
        payout={selectedPayout}
        onClose={() => {
          setSelectedPayout(null);
          setApproveError("");
        }}
        onConfirm={handleApprove}
        approving={approving}
        error={approveError}
      />
    </div>
  );
};

export default Payouts;
